/**
 * 通用函数模块
 * @module util
 */

import { get, set, debounce, throttle, cloneDeep, merge, uniqueId, isEqual } from 'lodash-es';
import Compressor from 'compressorjs';

/**
 * 根据path查找对象或数组中的某个属性
 * @static
 * @method get
 * @param {object|array} object 要检索的对象
 * @param {string|array} path 要获取属性的路径
 * @param {string} [defaultValue] 如果解析值是 undefined ，这值会被返回。
 * @return {undefined|*}
 */

/**
 * 设置 object对象中对应 path 属性路径上的值，如果path不存在，则创建。 缺少的索引属性会创建为数组，而缺少的属性会创建为对象
 * @static
 * @method set
 * @param {object} object 要修改的对象
 * @param {string|array} path 要设置的对象路径
 * @param {*} value 要设置的值
 */

/**
 * 判断两个值是否相等，支持引用类型的判断
 * @static
 * @method isEqual
 * @param {any} value1
 * @param {any} value2
 * @return {boolean}
 */

/**
 * 深拷贝
 * @static
 * @method cloneDeep
 * @param {*} value  要深拷贝的值
 * @return {*} 返回拷贝后的值
 */

/**
 * 创建防抖动函数
 * @static
 * @method debounce
 * @param {Function} func 要防抖动的函数
 * @param {number} [wait=0]  需要延迟的毫秒数
 * @param {object} [options] 选项对象
 * @param {boolean} [options.leading=false] 指定在延迟开始前调用。
 * @param {number} [options.maxWait] 设置 func 允许被延迟的最大值。
 * @param {boolean} [options.trailing=true] 指定在延迟结束后调用
 * @return {Function} 抖动函数
 */

/**
 * 创建一个节流函数
 * @static
 * @method throttle
 * @param {Function} func 要节流的函数
 * @param {number} [wait=0]   需要节流的毫秒
 * @param {object} [options] 选项对象
 * @param {boolean} [options.leading=false] 指定在延迟开始前调用。
 * @param {boolean} [options.trailing=true] 指定在延迟结束后调用
 * @return {Function} 节流函数
 */

/**
 * 对象合并
 * @static
 * @method merge
 * @param {Object} target 目标对象
 * @param {Object} [sources] 来源对象
 * @return {Object}
 */

/**
 * 转换字符串string为驼峰写法。
 * @static
 * @method camelCase
 * @param {string} [str] string 要转换的字符串
 * @return {string} 返回驼峰写法的字符串
 */

/**
 * 转换字符串string的首字母为大写。
 * @static
 * @method upperFirst
 * @param {string} [str] 要转换的字符串
 * @return {string}  返回转换后的字符串
 */

/**
 * 转换字符串string的首字母为大写。
 * @static
 * @method upperFirst
 * @param {string} [str] 要转换的字符串
 * @return {string}  返回转换后的字符串
 */

/**
 * 转换字符串string的首字母为小写。
 * @static
 * @method lowerFirst
 * @param {string} [str] 要转换的字符串
 * @return {string}  返回转换后的字符串
 */

/**
 * 转换字符串string为kebab case.
 * @static
 * @method kebabCase
 * @param {string} [str] string 要转换的字符串
 * @return {string} 返回驼峰写法的字符串
 */

/**
 * 创建一个去重后的array数组副本。使用了SameValueZero 做等值比较。只有第一次出现的元素才会被保留。
 * @static
 * @method uniq
 * @param {array} array 要检查的数组
 * @return {string} 返回新的去重后的数组
 */

export {
  get,
  set,
  isEqual,
  debounce,
  throttle,
  cloneDeep,
  merge,
  uniqueId
};

/**
 * 空函数，用来占位或默认值
 */
export function noop() {
}

/**
 * 判断对象是否有该属性
 * @param {object} obj
 * @param {string} key
 * @return {boolean}
 */
export function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * 生成唯一ID。 如果提供了 prefix ，会被添加到ID前缀上。
 * @param {string} [prefix] 要添加到ID前缀的值。
 * @param {boolean} [ts] 生成的Id是否增加时间戳
 * @return {string} 返回唯一ID
 */
export function uid(prefix = '', ts = true) {
  const timestamp = ts ? new Date().getTime() : '';
  return [prefix, timestamp, uniqueId()].join('');
}

/**
 * 创建GUID
 * @returns {string}
 */
export function uuid(split = true) {
  const id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
  return split ? id.toLowerCase() : id.replace(/-/gi, '');
}

/**
 * 对数组按制定字段名称进行分组
 * @param {Array} data 数组数组
 * @param {string} [field=group] 分组字段名称
 * @returns {object} 结果
 *
 * @example
 * [{name:1, group:'a'},{name:2, group:'a'}, {name:3, group:'b'}]  ->
 * {
 *  'a':[{name:1, group:'a'}, {name:2, group:'a'}]
 *  'b': [{name:3, group:'b'}]
 * }
 */
export function grouping(data = [], field = 'group') {
  const result = {
    default: []
  };
  data.forEach(item => {
    const group = item[field];
    if (group) {
      if (!result[group]) {
        result[group] = [];
      }
      result[group].push(item);
    } else {
      result.default.push(item);
    }
  });
  return result;
}

/**
 * 分页获取数据
 * @param {Array} data 源数据
 * @param {number} [page=1] 当前页面，1开始
 * @param {number} [size=10] 页大小，默认10
 * @return {Array}
 */
export function pager(data, page = 1, size = 10) {
  if (!data) return [];
  const start = (page - 1) * size;
  const end = start + size;
  return data.length > start ? data.slice(start, end) : [];
}

/**
 * 提取对象属性
 * @param {object} object
 * @param {function} [filter] 默认过滤 null、undefined
 * @returns {object}
 */
export function pick(object, filter) {
  const obj = Object.create(null);
  const match = filter || (v => v !== null && v !== undefined);
  Object.entries(object).forEach(([n, v]) => {
    if (match(v, n)) {
      obj[n] = v;
    }
  });
  return obj;
}

/**
 * 截取几位小数
 * @param {number} value 浮点数
 * @param {number} [number=2] 保留几位小数
 * @param {boolean} [round] 是否四舍五入
 * @returns {number}
 */
export function toFixed(value, number = 2, round) {
  const method = round ? Math.round : Math.floor;
  return method(Math.pow(10, number) * value) / Math.pow(10, number);
}

/**
 * 递归对象或数组清除文本类型值的两边空格
 * @param {Object|Array} obj
 * @return {Object|Array}
 */
export function trim(obj) {
  const type = typeof obj;
  if (type === 'string') {
    return obj.trim();
  }
  if (Array.isArray(obj)) {
    return obj.map(n => trim(n));
  }
  if (obj && type === 'object') {
    Object.entries(obj).forEach(([k, v]) => {
      obj[k] = trim(v);
    });
    return obj;
  }
  return obj;
}

/**
 * 获取文本字节数
 * @param {string} str 文本内容
 * @return {number}
 */
export function getCharLength(str = '') {
  let length = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    if (str.charCodeAt(i) > 255) {
      length += 2;
    } else {
      length += 1;
    }
  }
  return length;
}

/**
 * 获取数据类型
 * @param {*} data 任何数据
 * @return {string} 数据类型，如：Number,String,Boolean,Object,Array,Date,Null,Undefined
 */
export function getType(data) {
  const str = Object.prototype.toString.call(data);
  const result = str.match(/\[object ([\w]+)\]/);
  return (result && result.length > 1)
    ? result[1]
    : '';
}

export function toChineseMoney(n) {
  if (n === 0) {
    return '零';
  }
  if (!/^(\+|-)?(0|[1-9]\d*)(\.\d+)?$/.test(n)) {
    return '数据非法';
  }
  let unit = '仟佰拾亿仟佰拾万仟佰拾元角分';
  let str = '';
  n += '00';
  const a = parseFloat(n);
  if (a < 0) {
    n = n.substr(1);
  }
  const p = n.indexOf('.');
  if (p >= 0) {
    n = n.substring(0, p) + n.substr(p + 1, 2);
  }

  unit = unit.substr(unit.length - n.length);

  for (let i = 0; i < n.length; i++) {
    str += '零壹贰叁肆伍陆柒捌玖'.charAt(n.charAt(i)) + unit.charAt(i);
  }
  if (a > 0) {
    return str.replace(/零([仟佰拾角])/g, '零').replace(/(零)+/g, '零').replace(/零([万亿元])/g, '$1').replace(/(亿)万|壹(拾)/g, '$1$2').replace(/^元零?|零分/g, '').replace(/元$/g, '圆整');
  } else {
    return '负' + str.replace(/零([仟佰拾角])/g, '零').replace(/(零)+/g, '零').replace(/零([万亿元])/g, '$1').replace(/(亿)万|壹(拾)/g, '$1$2').replace(/^元零?|零分/g, '').replace(/元$/g, '圆整');
  }
}

/**
 * 压缩图片
 * docs: https://github.com/fengyuanchen/compressorjs/blob/main/README.md
 * @param {File|Blob} file The target image file for compressing.
 * @param options
 * @returns {Promise<unknown>}
 */
export function compressor(file, options = {}) {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line no-new
    new Compressor(file, {
      quality: 0.8,
      ...options,
      success(newFile) {
        resolve(newFile);
      },
      error(err) {
        reject(err);
      }
    });
  });
}

/**
 * 数字格式
 * value:值
 * decimalsLen: 保留几位小数
 * integerLen: 整数长度
 * */
export function numberFormat(value, decimalsLen, integerLen = 9) {
  value = String(value);
  if (decimalsLen) {
    value = value.replace(/[^0-9.]/ig, '');
  }
  if (!decimalsLen) {
    value = value.replace(/[^0-9]/ig, '');
    let arr = value.split('');
    if (arr[0] === '0') {
      return '0';
    }
    if (arr.length > integerLen) {
      arr = arr.splice(0, integerLen);
    }
    return arr.join('');
  }
  let dotIndex = 0;
  for (let i = 0; i < value.length; i++) {
    if (value[i] === '.') {
      dotIndex = i;
      break;
    }
  }
  value = value.replace(/\./ig, '');
  value = value.split('');
  if (dotIndex !== 0) {
    value.splice(dotIndex, 0, '.');
  }
  value = value.join('');
  const arr = value.split('.');
  if (arr[0].indexOf('0') === 0) arr[0] = 0;
  if (arr[0].length > integerLen) {
    arr[0] = arr[0].substring(0, integerLen);
  }
  if (arr[1] && arr[1].length > decimalsLen) {
    arr[1] = arr[1].slice(0, decimalsLen);
  }
  if (arr.length === 2) value = arr[0] + '.' + arr[1];
  if (dotIndex === 0) value = arr[0];
  return value;
}

/**
 * 启动后门代码
 * @param callback 回调函数
 * @param num 操作次数
 * @return {{destroy(): void}}
 */
export function initBackDoor(callback, num = 5) {
  let timer = 0;
  let count = 0;
  const handler = () => {
    clearTimeout(timer);
    ++count;
    if (count % num === 0) {
      count = 0;
      callback && callback();
      return;
    }
    timer = setTimeout(() => {
      count = 0;
    }, 1000);
  };
  window.addEventListener('click', handler, true);
  return {
    destroy() {
      window.removeEventListener('click', handler, true);
    }
  };
}

export default {
  get,
  set,
  isEqual,
  debounce,
  throttle,
  cloneDeep,
  merge,
  uniqueId,
  noop,
  uid,
  uuid,
  grouping,
  pager,
  pick,
  toFixed,
  trim,
  getCharLength,
  getType,
  toChineseMoney,
  compressor,
  numberFormat
};
